import React, { useState } from "react";
import { Button, notification } from "antd";
import { useRetryPanelSurveyPayoutMutation } from "../../store/api/apiSlice";
import {
  clearSurveyPayoutState,
  persistSurveyPayoutState,
  showPayoutSnackbar,
} from "../../utils/surveyPayout";

/**
 * Retry Tremendous reward for a panel survey token.
 */
const RetryRewardButton = ({
  token,
  formId,
  className = "",
  onSuccess,
  onPayoutUpdate,
}) => {
  const [retryPanelSurveyPayout, { isLoading }] =
    useRetryPanelSurveyPayoutMutation();
  const [localLoading, setLocalLoading] = useState(false);

  if (!token) return null;

  const handleRetry = async () => {
    setLocalLoading(true);

    try {
      const result = await retryPanelSurveyPayout({ token }).unwrap();
      const data = result?.data ?? result;
      const snackbar = data?.snackbar || data?.payout?.snackbar;
      const payout = data?.payout || {
        payoutSent: Boolean(data?.payoutSent),
        payoutError: data?.payoutError ?? null,
        canRetry: Boolean(data?.canRetry),
        orderId: data?.orderId ?? null,
        email: data?.email ?? null,
        alreadyCompleted: Boolean(data?.alreadyCompleted),
        snackbar: snackbar || null,
      };

      if (formId) {
        persistSurveyPayoutState(formId, payout);
      }

      if (snackbar) {
        showPayoutSnackbar(snackbar, {
          canRetry: Boolean(payout?.canRetry),
          onRetry: payout?.canRetry ? handleRetry : undefined,
        });
      } else if (payout?.payoutError) {
        showPayoutSnackbar(
          { severity: "error", message: payout.payoutError },
          {
            canRetry: Boolean(payout?.canRetry),
            onRetry: payout?.canRetry ? handleRetry : undefined,
          },
        );
      } else if (payout?.payoutSent) {
        showPayoutSnackbar({
          severity: "success",
          message: "Reward email sent successfully",
        });
      }

      onPayoutUpdate?.(payout);

      if (payout?.payoutSent && formId) {
        clearSurveyPayoutState(formId);
      }

      onSuccess?.(payout);
    } catch (error) {
      const apiMessage =
        error?.data?.message ||
        error?.data?.payoutError ||
        error?.data?.data?.payoutError ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to retry reward payout";

      notification.error({
        message: "Reward payout failed",
        description: String(apiMessage),
        duration: 10,
        placement: "topRight",
        style: { whiteSpace: "pre-wrap", maxWidth: 440 },
        btn: (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              notification.destroy();
              handleRetry();
            }}
          >
            Retry reward
          </Button>
        ),
      });
    } finally {
      setLocalLoading(false);
    }
  };

  return (
    <Button
      type="primary"
      loading={isLoading || localLoading}
      onClick={handleRetry}
      className={
        className ||
        "h-11 min-h-[44px] rounded-2xl border-none bg-[#202835] px-5 text-base font-medium hover:bg-[#2f3b4d]"
      }
    >
      Retry reward
    </Button>
  );
};

export default RetryRewardButton;
